import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { generateArticleJsonLd, generateFAQJsonLd, getSiteUrl } from "@/utils/seo";

const reviewFAQ = [
  { q: "How long does Sugargoo take to process orders?", a: "Sugargoo typically processes orders within 24-48 hours after payment confirmation. The seller then ships to the Sugargoo warehouse, which takes 3-7 days on average depending on the seller's location and shipping method." },
  { q: "Can I get a refund if the product is defective?", a: "Yes, Sugargoo offers a dispute process for defective or incorrect items. QC photos taken at the warehouse help document any issues. Refund success depends on the seller's policies and the evidence provided through QC photos." },
  { q: "Does Sugargoo charge for warehouse storage?", a: "Sugargoo offers free storage for up to 90 days. After this period, storage fees may apply. This generous free period allows ample time to accumulate multiple purchases for consolidated shipping." },
  { q: "What payment methods does Sugargoo accept?", a: "Sugargoo accepts PayPal, credit/debit cards, bank transfers, and various regional payment methods. PayPal is recommended for its buyer protection features, especially for first-time users." },
];

export default function ReviewPage() {
  const siteUrl = getSiteUrl();
  const pageUrl = `${siteUrl}/sugargoo-review`;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(generateArticleJsonLd({
          url: pageUrl,
          headline: "Sugargoo Review 2026: Complete Platform Analysis & Honest Rating",
          description: "An independent, research-backed review of the Sugargoo shopping platform. We examine features, pricing, shipping performance, customer support quality, and overall value to help you decide if Sugargoo is the right shopping agent for your needs.",
        })) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(generateFAQJsonLd(reviewFAQ)) }}
      />
      <Navbar />
      <main>
        <article className="w-full px-6 md:px-10 pt-24 pb-16">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">Home</Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <span className="text-foreground-700 whitespace-nowrap">Sugargoo Review 2026</span>
            </nav>

            <header className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold whitespace-nowrap">In-Depth Review</span>
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold whitespace-nowrap">Updated 2026</span>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-tight mb-4">
                Sugargoo Review 2026: Complete Platform Analysis &amp; Honest Rating
              </h1>
              <p className="text-foreground-600 text-base leading-relaxed">
                An independent, research-backed review of the Sugargoo shopping platform. We examine features, pricing, shipping performance, customer support quality, and overall value to help you decide if Sugargoo is the right shopping agent for your needs.
              </p>
            </header>

            <div className="prose-custom space-y-8">
              <section>
                <h2 className="font-heading text-2xl text-foreground-950 mb-4">What Is Sugargoo? A Brief Platform Overview</h2>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  Sugargoo is a China-based shopping agent platform that enables international buyers to purchase products from Chinese e-commerce marketplaces including Taobao, Weidian, and 1688. Founded to bridge the gap between global shoppers and Chinese retail platforms, Sugargoo handles the entire purchasing workflow from order placement through international delivery.
                </p>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  The platform operates on a simple model: you find products on Chinese marketplaces, submit the links to Sugargoo, and their team purchases the items on your behalf. Products are shipped to Sugargoo&apos;s warehouse where they undergo quality control inspection before being forwarded to your international address. This model eliminates language barriers, payment complexities, and the logistical challenges of cross-border shopping.
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  Sugargoo has grown significantly since its launch, now serving customers across North America, Europe, Australia, and Asia. The platform supports multiple languages, various payment methods including PayPal and credit cards, and offers a range of international shipping options from budget economy lines to premium express couriers.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-foreground-950 mb-4">Sugargoo Core Features: What You Get</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                  {[
                    { title: "Product Sourcing", desc: "Submit any product URL from Taobao, Weidian, or 1688, and Sugargoo handles the purchase. No account registration needed on Chinese platforms." },
                    { title: "Quality Control Photos", desc: "Free QC photos taken at the warehouse showing actual products before international shipping. Verify condition, color accuracy, sizing labels, and any defects." },
                    { title: "Warehouse Storage", desc: "Free storage for up to 90 days, allowing you to accumulate multiple purchases and ship them together in one consolidated package to save on shipping costs." },
                    { title: "Package Consolidation", desc: "Combine items from multiple orders into a single international shipment. Sugargoo removes excess packaging to reduce volumetric weight and shipping fees." },
                    { title: "Multiple Shipping Lines", desc: "Access to EMS, DHL, FedEx, UPS, SAL, EUB, and various economy lines. Each option offers different speed and cost trade-offs for different destinations." },
                    { title: "Customer Support", desc: "24/7 online customer service via live chat, ticket system, and email. Support available in English, Chinese, and several other languages." },
                  ].map((feat, i) => (
                    <div key={i} className="p-4 rounded-xl bg-background-100 border border-background-200/70">
                      <h4 className="font-semibold text-sm text-foreground-900 mb-1.5">{feat.title}</h4>
                      <p className="text-xs text-foreground-600 leading-relaxed">{feat.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-foreground-950 mb-4">Sugargoo Pros and Cons: Balanced Assessment</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 my-6">
                  <div className="p-6 rounded-xl bg-accent-50 border border-accent-200/60">
                    <h3 className="flex items-center gap-2 font-semibold text-foreground-900 mb-4 text-sm">
                      <i className="ri-thumb-up-line text-accent-500"></i>
                      Pros / Advantages
                    </h3>
                    <ul className="space-y-2.5">
                      {[
                        "User-friendly interface with intuitive navigation for beginners",
                        "Free high-quality QC photos for every order placed",
                        "Competitive service fees compared to other shopping agents",
                        "Extensive shipping options covering budget to premium needs",
                        "Long free warehouse storage period of up to 90 days",
                        "Multiple payment methods including PayPal for buyer protection",
                        "Active community with shared spreadsheets and product finds",
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-foreground-700">
                          <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-6 rounded-xl bg-primary-50 border border-primary-200/60">
                    <h3 className="flex items-center gap-2 font-semibold text-foreground-900 mb-4 text-sm">
                      <i className="ri-thumb-down-line text-primary-500"></i>
                      Cons / Limitations
                    </h3>
                    <ul className="space-y-2.5">
                      {[
                        "Shipping costs can be high for heavy items like shoes and jackets",
                        "Exchange rate margins on currency conversion add hidden costs",
                        "Customer support response times vary during peak shopping seasons",
                        "Limited direct brand relationships; mostly reliant on third-party sellers",
                        "No mobile app available, only web-based platform access",
                        "QC photos are sometimes basic and may not capture all details",
                        "Return and refund processes can be slow for disputed items",
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-foreground-700">
                          <i className="ri-close-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-foreground-950 mb-4">Sugargoo Pricing: Service Fees and Hidden Costs</h2>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  Sugargoo charges a service fee for each order, typically calculated as a percentage of the product price plus a small fixed fee. For most categories, the service fee ranges from 5% to 10% of the item cost, making it one of the more affordable shopping agents on the market. However, there are additional costs to factor into your total expense calculation.
                </p>
                <div className="overflow-x-auto rounded-xl border border-background-200/70 my-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-200/70 bg-secondary-50">
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">Cost Component</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">Typical Range</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { component: "Service Fee", range: "5% - 10% of item price", notes: "Varies by product category" },
                        { component: "Domestic Shipping", range: "$1 - $5 per order", notes: "Shipping from seller to Sugargoo warehouse" },
                        { component: "International Shipping", range: "$15 - $60+ per kg", notes: "Depends on method and destination" },
                        { component: "Currency Conversion", range: "2% - 4% margin", notes: "Built into exchange rate offered" },
                        { component: "Payment Processing", range: "$0 - $3 per transaction", notes: "Free for some payment methods" },
                        { component: "Additional QC Photos", range: "$1 - $3 per set", notes: "First set is usually free" },
                      ].map((row, i) => (
                        <tr key={i} className={`border-b border-background-200/50 ${i % 2 === 0 ? "bg-background-50" : "bg-secondary-50/50"}`}>
                          <td className="px-4 py-3 font-medium text-foreground-800 whitespace-nowrap">{row.component}</td>
                          <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{row.range}</td>
                          <td className="px-4 py-3 text-xs text-foreground-600">{row.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-foreground-950 mb-4">Sugargoo vs Other Shopping Agents</h2>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  When comparing Sugargoo with other popular shopping agents, several key differences emerge. Sugargoo generally offers more competitive service fees than older platforms while providing comparable warehouse and shipping services. The QC photo quality is rated as above average by community reviews, though some competitors offer more detailed inspection reports.
                </p>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  In terms of shipping speed, Sugargoo&apos;s processing time (from order placement to warehouse arrival) averages 3-7 days, which is competitive with industry standards. International shipping times vary by method but generally align with carrier estimates. The platform&apos;s package consolidation service is particularly efficient, with most consolidation requests processed within 24-48 hours.
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  For users who rely heavily on community resources like spreadsheets and shared product finds, Sugargoo has built one of the most active user communities. The abundance of Sugargoo Spreadsheet resources available online makes product discovery significantly easier compared to platforms with less community engagement.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-foreground-950 mb-4">Final Verdict: Is Sugargoo Worth Using in 2026?</h2>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  After extensive testing and community research, we rate Sugargoo as a solid choice for international shoppers, particularly those new to Chinese marketplace purchasing. The platform strikes a good balance between affordability, functionality, and user experience.
                </p>
                <div className="bg-background-100 rounded-xl border border-background-200/70 p-6 my-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-100">
                      <span className="font-heading text-2xl text-primary-600">8.2</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground-900 text-sm">Our Rating: 8.2 / 10</h3>
                      <p className="text-xs text-foreground-600">Based on pricing, features, support, and community feedback</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {[
                      { label: "Value for Money", score: 85 },
                      { label: "Ease of Use", score: 88 },
                      { label: "Shipping Options", score: 82 },
                      { label: "Customer Support", score: 75 },
                      { label: "QC Quality", score: 80 },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs text-foreground-700 w-32 whitespace-nowrap">{item.label}</span>
                        <div className="flex-1 h-2 rounded-full bg-background-200 overflow-hidden">
                          <div className="h-full rounded-full bg-primary-500 transition-all" style={{ width: `${item.score}%` }}></div>
                        </div>
                        <span className="text-xs font-semibold text-foreground-800 w-8 text-right">{item.score}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl text-foreground-950 mb-4">Frequently Asked Questions</h2>
                <div className="space-y-3">
                  {[
                    { q: "How long does Sugargoo take to process orders?", a: "Sugargoo typically processes orders within 24-48 hours after payment confirmation. The seller then ships to the Sugargoo warehouse, which takes 3-7 days on average depending on the seller&apos;s location and shipping method." },
                    { q: "Can I get a refund if the product is defective?", a: "Yes, Sugargoo offers a dispute process for defective or incorrect items. QC photos taken at the warehouse help document any issues. Refund success depends on the seller&apos;s policies and the evidence provided through QC photos." },
                    { q: "Does Sugargoo charge for warehouse storage?", a: "Sugargoo offers free storage for up to 90 days. After this period, storage fees may apply. This generous free period allows ample time to accumulate multiple purchases for consolidated shipping." },
                    { q: "What payment methods does Sugargoo accept?", a: "Sugargoo accepts PayPal, credit/debit cards, bank transfers, and various regional payment methods. PayPal is recommended for its buyer protection features, especially for first-time users." },
                  ].map((faq, i) => (
                    <details key={i} className="group rounded-xl border border-background-200/70 bg-background-100 overflow-hidden">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-sm text-foreground-800">
                        {faq.q}
                        <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0 ml-2"></i>
                        <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0 ml-2"></i>
                      </summary>
                      <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
                    </details>
                  ))}
                </div>
              </section>

              <section className="pt-6">
                <div className="rounded-2xl bg-primary-500/5 border border-primary-200/50 p-8 text-center">
                  <h2 className="font-heading text-xl text-foreground-950 mb-3">Ready to Try Sugargoo?</h2>
                  <p className="text-foreground-600 text-sm mb-6 max-w-md mx-auto">
                    Explore our curated Sugargoo Spreadsheet categories and start shopping smarter with organized product databases.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link to="/" className="w-full sm:w-auto px-7 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                      Back to Homepage
                    </Link>
                    <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-full sm:w-auto px-7 py-3 rounded-full bg-background-100 text-foreground-800 font-semibold text-sm border border-background-200/70 hover:bg-background-200/50 transition-colors cursor-pointer whitespace-nowrap">
                      Visit Main Website
                    </a>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}